import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { User, LogOut, Settings, Radio, Mic2, AlertTriangle, HelpCircle, Globe } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';
import HelpModal from '@/components/HelpModal';
import useLanguage from '@/hooks/useLanguage';

const ADMIN_EMAILS = ["didier2marchand@gmail.com", "test@hearen.app"];

const Profile = ({ t: tProp, onLanguageChange }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { lang: currentLang, t: tHook, changeLanguage } = useLanguage();
  const isAdmin = user?.email && ADMIN_EMAILS.map(e => e.toLowerCase()).includes(user.email.toLowerCase());
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  
  // Use hook translations (reactive) or prop fallback
  const t = tHook || tProp;
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  
  // Help & Support modal state
  const [showHelpModal, setShowHelpModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleResetOnboarding = async () => {
    setIsResetting(true);

    try {
      await api.resetOnboarding();
      
      // Clear local storage draft
      localStorage.removeItem('hearen-onboarding-draft');
      localStorage.removeItem('hearen-onboarding-defers');
      // Redirect to onboarding
      navigate('/onboarding');
    } catch (error) {
      console.error('❌ Error resetting onboarding:', error);
      alert('Erreur lors de la réinitialisation. Veuillez réessayer.');
      setShowResetConfirm(false);
    } finally {
      setIsResetting(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t?.passwordMismatch || 'Les mots de passe ne correspondent pas');
      return;
    }

    // Validate new password length
    if (passwordData.newPassword.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      const response = await api.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('❌ Error changing password:', error);
      setPasswordError(error.response?.data?.message || t?.passwordError || 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-48 relative overflow-hidden">
      {/* Halo top-left violet premium */}
      <div className="absolute -top-32 -left-20 w-[320px] h-[320px] bg-[#6F42EA] opacity-[0.12] blur-[130px] rounded-full pointer-events-none" />
      
      {/* Halo bottom-right bleu premium */}
      <div className="absolute bottom-0 right-[-40px] w-[280px] h-[280px] bg-[#3B48EE] opacity-[0.10] blur-[140px] rounded-full pointer-events-none" />
      
      {/* Grain fin premium */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          mixBlendMode: 'overlay'
        }}
      />

      <div className="max-w-screen-xl mx-auto px-4 pt-20 pb-6 relative z-10">
        <PageHeader t={t} />

        <div className="max-w-md mx-auto space-y-4 mt-6">
          {/* User Info Card - Premium */}
          <Card className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[3px] border border-white/10 shadow-[0_6px_28px_rgba(0,0,0,0.45)] animate-fade-slide-up">
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
            
            <CardHeader className="pb-3 relative z-10">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/15 shadow-md">
                  <User size={32} className="text-white/80" />
                </div>
                <div>
                  <CardTitle className="text-lg text-white font-semibold tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">{user?.name}</CardTitle>
                  <p className="text-sm text-white/70 mt-0.5">{user?.email}</p>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Admin Options - Premium */}
          {isAdmin && (
            <>
              <Card 
                className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[3px] border border-white/10 shadow-[0_6px_28px_rgba(0,0,0,0.45)] cursor-pointer transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group animate-fade-slide-up"
                onClick={() => navigate('/admin')}
              >
                {/* Gradient overlay avec teinte bleu */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/20 to-[#6F42EA]/10 pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
                
                <CardContent className="p-4 relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/15 shadow-md">
                        <Settings size={22} className="text-white/80" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">🛠️ {t?.adminTitle || 'Administration'}</h3>
                        <p className="text-xs text-white/80 mt-0.5">{t?.adminDescription || 'Bibliothèque & Messages Vocaux'}</p>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-white/60 group-hover:text-white transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Help & Support Section - Premium */}
          <Card 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#151A24] to-[#1D2333] border border-white/8 shadow-[0_8px_32px_rgba(72,52,212,0.25)] cursor-pointer transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group animate-fade-slide-up"
            onClick={() => setShowHelpModal(true)}
          >
            {/* Gradient overlay subtil */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#6F42EA]/15 to-[#3B48EE]/10 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
            
            <CardContent className="p-4 relative z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/15 shadow-md">
                    <HelpCircle size={22} className="text-white/80" style={{ filter: 'drop-shadow(0 0 4px rgba(111, 66, 234, 0.3))' }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">
                      {t?.helpSupportTitle || 'Help & Support'}
                    </h3>
                    <p className="text-xs text-white/80 mt-0.5">
                      {t?.helpSupportSubtitle || 'Assistance, questions fréquentes et informations utiles'}
                    </p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-white/60 group-hover:text-white transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </CardContent>
          </Card>

          {/* Language Selection - Centralized */}
          <Card 
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#151A24] to-[#1D2333] border border-white/8 shadow-[0_8px_32px_rgba(72,52,212,0.25)] cursor-pointer transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group animate-fade-slide-up"
            onClick={() => setShowLanguageModal(true)}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/15 to-[#6F42EA]/10 pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
            
            <CardContent className="p-4 relative z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/15 shadow-md">
                    <Globe size={22} className="text-white/80" style={{ filter: 'drop-shadow(0 0 4px rgba(59, 72, 238, 0.3))' }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">
                      🌍 {currentLang === 'en' ? 'Language' : 'Langue'}
                    </h3>
                    <p className="text-xs text-white/80 mt-0.5">
                      {currentLang === 'en' ? 'Change app language' : 'Changer la langue de l\'app'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-2 py-1 bg-white/15 rounded-full text-white/90">
                    {currentLang === 'fr' ? 'FR' : 'EN'}
                  </span>
                  <svg className="w-5 h-5 text-white/60 group-hover:text-white transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password Section - Premium */}
          {!showPasswordForm ? (
            <Button
              onClick={() => setShowPasswordForm(true)}
              className="w-full relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[4px] border border-white/10 py-6 font-medium text-base transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group animate-fade-slide-up"
              style={{ boxShadow: '0 6px 28px rgba(0,0,0,0.45)' }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-green-500/10 pointer-events-none" />
              <div className="relative z-10 flex items-center justify-center text-white">
                <Settings className="mr-2" size={20} />
                🔒 {t?.changePassword || 'Changer le mot de passe'}
              </div>
            </Button>
          ) : (
            <Card className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[4px] border border-white/10 shadow-lg animate-fade-slide-up">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-green-500/10 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
              <CardContent className="p-4 relative z-10">
                <h4 className="font-semibold text-white tracking-wide mb-4 drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">{t?.changePassword || 'Changer le mot de passe'}</h4>
                
                {passwordSuccess && (
                  <div className="mb-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg text-green-100 text-sm backdrop-blur-sm">
                    ✅ {t?.passwordUpdated || 'Mot de passe mis à jour avec succès'}
                  </div>
                )}
                
                {passwordError && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-100 text-sm backdrop-blur-sm">
                    ❌ {passwordError}
                  </div>
                )}

                <form onSubmit={handlePasswordChange} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-1">
                      {t?.currentPassword || 'Mot de passe actuel'}
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 text-white placeholder-white/40 backdrop-blur-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-1">
                      {t?.newPassword || 'Nouveau mot de passe'}
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 text-white placeholder-white/40 backdrop-blur-sm"
                      required
                      minLength={6}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-1">
                      {t?.confirmPassword || 'Confirmer le mot de passe'}
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 text-white placeholder-white/40 backdrop-blur-sm"
                      required
                      minLength={6}
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      type="submit"
                      disabled={isUpdatingPassword}
                      className="flex-1 bg-gradient-to-br from-green-500 to-green-600 hover:brightness-110 text-white rounded-lg py-3 shadow-md transition"
                    >
                      {isUpdatingPassword ? '...' : (t?.updatePassword || 'Mettre à jour')}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setPasswordError('');
                      }}
                      disabled={isUpdatingPassword}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-lg py-3 backdrop-blur-sm border border-white/10 transition"
                    >
                      {t?.cancel || 'Annuler'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Reset Onboarding Button (for testing) - Premium */}
          {!showResetConfirm ? (
            <div className="space-y-3 animate-fade-slide-up">
              <Button
                onClick={() => setShowResetConfirm(true)}
                className="w-full relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[4px] border border-white/10 py-6 font-medium text-base transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group"
                style={{ boxShadow: '0 6px 28px rgba(0,0,0,0.45)' }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#3B48EE]/20 to-[#6F42EA]/10 pointer-events-none" />
                <div className="relative z-10 flex items-center justify-center text-white">
                  <Settings className="mr-2" size={20} />
                  🧪 {t?.resetOnboarding || 'Réinitialiser Onboarding (Test)'}
                </div>
              </Button>
              
              {/* Alternative: Direct link */}
              <Button
                onClick={() => navigate('/onboarding')}
                className="w-full relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[4px] border border-white/10 py-4 font-medium text-sm transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97]"
                style={{ boxShadow: '0 6px 28px rgba(0,0,0,0.45)' }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#6F42EA]/20 to-[#3B48EE]/10 pointer-events-none" />
                <div className="relative z-10 text-white">
                  📋 {t?.viewOnboarding || 'Voir l\'Onboarding'}
                </div>
              </Button>
            </div>
          ) : (
            <Card className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[4px] border border-white/10 shadow-lg animate-fade-slide-up">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-yellow-500/10 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
              <CardContent className="p-4 relative z-10">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="text-yellow-300 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="font-semibold text-white tracking-wide mb-1 drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">{t?.confirmReset || 'Confirmer la réinitialisation'}</h4>
                    <p className="text-sm text-white/80">
                      {t?.confirmResetMessage || 'Voulez-vous vraiment réinitialiser votre profil onboarding ? Vous pourrez refaire le questionnaire complet.'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleResetOnboarding}
                    disabled={isResetting}
                    className="flex-1 bg-gradient-to-br from-[#3B48EE] to-[#6F42EA] hover:brightness-110 text-white rounded-lg py-3 shadow-md transition"
                  >
                    {isResetting ? (t?.resetting || 'Réinitialisation...') : (t?.yesReset || 'Oui, réinitialiser')}
                  </Button>
                  <Button
                    onClick={() => setShowResetConfirm(false)}
                    disabled={isResetting}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-lg py-3 backdrop-blur-sm border border-white/10 transition"
                  >
                    {t?.cancel || 'Annuler'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Logout Button - Premium */}
          <Button
            onClick={handleLogout}
            className="w-full relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[4px] border border-white/10 py-6 font-medium text-base transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group animate-fade-slide-up"
            style={{ boxShadow: '0 6px 28px rgba(0,0,0,0.45)' }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-red-500/10 pointer-events-none" />
            <div className="relative z-10 flex items-center justify-center text-white">
              <LogOut className="mr-2" size={20} />
              {t?.logout || 'Déconnexion'}
            </div>
          </Button>

          {/* V1.1: Section Avancé - Debug & Lab Mode */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <h3 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3 px-1">
              {currentLang === 'en' ? 'Advanced' : 'Avancé'}
            </h3>
            
            <div className="space-y-3">
              {/* Lab Mode (Experimental) */}
              <Card 
                className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[4px] border border-white/10 shadow-lg cursor-pointer transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group animate-fade-slide-up"
                onClick={() => navigate('/settings/lab')}
                data-testid="lab-mode-btn"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#6F42EA]/20 to-[#3B48EE]/10 pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
                
                <CardContent className="p-4 relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/15 shadow-md">
                        <Settings size={18} className="text-white/80" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">{t?.labTitle || 'Mode Lab'}</h3>
                          <span className="px-1.5 py-0.5 bg-gradient-to-br from-[#6F42EA] to-[#3B48EE] text-white text-[10px] font-semibold rounded-full shadow-md">
                            {t?.labBeta || 'Bêta'}
                          </span>
                        </div>
                        <p className="text-xs text-white/60 mt-0.5">{t?.labDescription || 'Notch & Inverse Notch'}</p>
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-white/40 group-hover:text-white/70 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </CardContent>
              </Card>

              {/* Debug Network - Admin Only */}
              {isAdmin && (
              <Card 
                className="relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-[3px] border border-white/10 shadow-lg cursor-pointer transition-all duration-300 ease-out hover:scale-[1.015] active:scale-[0.97] group animate-fade-slide-up"
                onClick={() => navigate('/admin/debug/network')}
                data-testid="debug-network-btn"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/15 to-amber-500/10 pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/20 to-transparent pointer-events-none" />
                
                <CardContent className="p-4 relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-xl flex items-center justify-center border border-white/15 shadow-md">
                        <Radio size={18} className="text-white/80" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-white tracking-wide drop-shadow-[0_4px_12px_rgba(0,0,0,0.45)]">
                          {currentLang === 'en' ? 'Network Debug' : 'Debug réseau'}
                        </h3>
                        <p className="text-xs text-white/60 mt-0.5">
                          {currentLang === 'en' ? 'Connection test, Health, API errors' : 'Test connexion, Health, Erreurs API'}
                        </p>
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-white/40 group-hover:text-white/70 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </CardContent>
              </Card>
              )}
            </div>
          </div>

          {/* Wellness Statement at bottom - Premium */}
          <div className="text-center px-6 py-4 mt-8">
            <p className="text-sm font-normal text-white/60 leading-relaxed">
              {t?.locale === 'fr-FR' 
                ? "HEAREN est une application de bien-être auditif, conçue pour vous aider à retrouver équilibre et confort sonore au quotidien."
                : "HEAREN is a wellness app designed to help you restore balance and auditory comfort in your daily life."}
            </p>
          </div>
        </div>
      </div>

      {/* Help & Support Modal */}
      <HelpModal 
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        title={t?.helpSupportTitle || 'Help & Support'}
      >
        {/* Section A - FAQ */}
        <div className="space-y-5">
          <div>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-2xl">💡</span> {t?.faqTitle || 'Questions Fréquentes'}
            </h3>
            <div className="space-y-4">
              {/* FAQ 1 */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="font-semibold text-white mb-2">
                  {t?.faq1Question || 'Pourquoi mon score varie-t-il ?'}
                </p>
                <p className="text-white/80 text-sm leading-relaxed">
                  {t?.faq1Answer || 'Les variations quotidiennes sont normales. Concentrez-vous sur la tendance générale plutôt que sur les variations jour par jour.'}
                </p>
              </div>

              {/* FAQ 2 */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="font-semibold text-white mb-2">
                  {t?.faq2Question || 'Je ne comprends pas le Notch / Inverse Notch.'}
                </p>
                <p className="text-white/80 text-sm leading-relaxed">
                  {t?.faq2Answer || 'Ce sont des filtres ciblés autour de votre fréquence acouphénique. Vous pouvez les activer/désactiver librement dans le Mode Lab.'}
                </p>
              </div>

              {/* FAQ 3 */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="font-semibold text-white mb-2">
                  {t?.faq3Question || 'Je n\'entends rien / son trop faible.'}
                </p>
                <p className="text-white/80 text-sm leading-relaxed">
                  {t?.faq3Answer || 'Vérifiez le volume de votre téléphone et de votre casque. Assurez-vous que le volume de l\'application est suffisant.'}
                </p>
              </div>

              {/* FAQ 4 */}
              <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 border border-white/10">
                <p className="font-semibold text-white mb-2">
                  {t?.faq4Question || 'Quand consulter un ORL ?'}
                </p>
                <p className="text-white/80 text-sm leading-relaxed">
                  {t?.faq4Answer || 'En cas d\'apparition brutale des acouphènes, de douleur intense, ou d\'anxiété importante, consultez un professionnel de santé.'}
                </p>
              </div>
            </div>
          </div>

          {/* Section B - Contact Support */}
          <div className="pt-4 border-t border-white/10">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-2xl">📧</span> {t?.contactSupportTitle || 'Contact Support'}
            </h3>
            <a 
              href={`mailto:${user?.email || 'support@hearen.app'}`}
              className="inline-block w-full"
            >
              <button className="w-full bg-gradient-to-br from-[#3B48EE] to-[#6F42EA] hover:brightness-110 text-white font-semibold py-4 px-6 rounded-2xl shadow-[0_0_24px_rgba(111,66,234,0.5)] transition-all active:scale-[0.97]">
                {t?.contactSupportButton || 'Contacter le support'}
              </button>
            </a>
          </div>

          {/* Section C - Sécurité & Bien-être */}
          <div className="pt-4 border-t border-white/10">
            <div className="bg-amber-500/15 backdrop-blur-md rounded-2xl p-5 border border-amber-500/30">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">⚠️</span>
                <div>
                  <h4 className="font-bold text-amber-100 mb-2">
                    {t?.wellbeingTitle || 'Sécurité & Bien-être'}
                  </h4>
                  <p className="text-amber-50/90 text-sm leading-relaxed">
                    {t?.wellbeingMessage || 'En cas d\'angoisse forte, de détresse ou d\'apparition brutale d\'acouphènes, contactez un professionnel de santé ou les numéros d\'urgence de votre pays.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HelpModal>

      {/* Animations CSS Premium */}
      <style>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-slide-up {
          animation: fadeSlideUp 200ms ease-out;
        }
      `}</style>

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowLanguageModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          
          {/* Modal */}
          <div 
            className="relative bg-[#1a1a24] border border-white/10 rounded-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'fadeSlideUp 200ms ease-out' }}
          >
            {/* Header */}
            <div className="p-5 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Globe className="w-5 h-5 text-violet-400" />
                {currentLang === 'en' ? 'Select Language' : 'Choisir la langue'}
              </h2>
            </div>
            
            {/* Language Options */}
            <div className="p-3 space-y-2">
              {/* French */}
              <button
                onClick={() => {
                  changeLanguage('fr');
                  onLanguageChange?.('fr');
                  setShowLanguageModal(false);
                }}
                className={`w-full p-4 rounded-xl flex items-center justify-between transition-all ${
                  currentLang === 'fr' 
                    ? 'bg-violet-500/20 border border-violet-500/40' 
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🇫🇷</span>
                  <div className="text-left">
                    <p className="text-white font-medium">Français</p>
                    <p className="text-white/50 text-xs">French</p>
                  </div>
                </div>
                {currentLang === 'fr' && (
                  <div className="w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
              
              {/* English */}
              <button
                onClick={() => {
                  changeLanguage('en');
                  onLanguageChange?.('en');
                  setShowLanguageModal(false);
                }}
                className={`w-full p-4 rounded-xl flex items-center justify-between transition-all ${
                  currentLang === 'en' 
                    ? 'bg-violet-500/20 border border-violet-500/40' 
                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🇬🇧</span>
                  <div className="text-left">
                    <p className="text-white font-medium">English</p>
                    <p className="text-white/50 text-xs">Anglais</p>
                  </div>
                </div>
                {currentLang === 'en' && (
                  <div className="w-5 h-5 rounded-full bg-violet-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-white/10">
              <button
                onClick={() => setShowLanguageModal(false)}
                className="w-full py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium transition-colors"
              >
                {currentLang === 'en' ? 'Cancel' : 'Annuler'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
